import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "../lib/api.js";
import { dayRangeLabel } from "../lib/dates.js";
import { useIsDesktop } from "../lib/media.js";
import { loadOccurrenceOrEvent, resolveOccurrence } from "../lib/occurrence.js";
import { describe as describeRepeat, fromRrule } from "../lib/repeat.js";
import { useSession } from "../lib/session.js";
import type { EventRecord } from "../lib/types.js";
import { foregroundOn, initialOf } from "../tokens/palette.js";

/**
 * What an event is, before anyone changes it.
 *
 * Tapping something on a calendar is nearly always a question — when is it,
 * where, who is going — and the answer used to be a form with the answer
 * scattered across its fields. This is the answer, laid out to be read; the
 * form is one deliberate tap further on, for the people allowed to use it.
 */
export function EventDetail(): React.JSX.Element {
  const { zone, canEdit } = useSession();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const occurrenceId = params.get("occ");

  const [event, setEvent] = useState<EventRecord | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (id === undefined) return;

    void (async () => {
      try {
        setEvent(await loadOccurrenceOrEvent(id, occurrenceId));
      } catch (error) {
        setFailure(
          error instanceof ApiError ? error.message : "That event couldn't be loaded.",
        );
      }
    })();
  }, [id, occurrenceId]);

  if (failure !== null) {
    return (
      <div className={`sheet${isDesktop ? " sheet--column" : ""}`}>
        <header className="sheet__head">
          <button type="button" onClick={() => void navigate(-1)}>
            Back
          </button>
          <span className="sheet__title">Event</span>
          <span />
        </header>
        <div className="sheet__body">
          <p className="error">{failure}</p>
        </div>
      </div>
    );
  }

  if (event === null) return <div className={`sheet${isDesktop ? " sheet--column" : ""}`} />;

  /*
   * The occurrence fetch already answers for its own day, so there is nothing
   * to re-derive; only the plain-event path needs the series unpicked.
   */
  const { start, end } = resolveOccurrence(event, null, zone);
  const repeat = fromRrule(event.rrule);
  const repeats = repeat.kind !== "none";

  return (
    <div className={`sheet${isDesktop ? " sheet--column" : ""}`}>
      <header className="sheet__head">
        <button type="button" onClick={() => void navigate(-1)}>
          Back
        </button>
        <span className="sheet__title">Event</span>
        {canEdit ? (
          <button
            type="button"
            className="sheet__save"
            onClick={() =>
              void navigate(
                `/event/${event.id}/edit${
                  occurrenceId === null ? "" : `?occ=${encodeURIComponent(occurrenceId)}`
                }`,
              )
            }
          >
            Edit
          </button>
        ) : (
          <span />
        )}
      </header>

      <div className="sheet__body detail">
        <h1 className={`detail__title${event.is_background ? " is-background" : ""}`}>
          {event.title}
        </h1>

        <div className="detail__when">
          <p className="detail__day">
            {dayRangeLabel(start.toISO() ?? event.starts_at, end?.toISO() ?? null, zone)}
          </p>
          <p className="detail__time">
            {event.all_day
              ? "All day"
              : end === null
                ? start.toFormat("h:mm a").toLowerCase()
                : `${start.toFormat("h:mm").toLowerCase()} – ${end
                    .toFormat("h:mm a")
                    .toLowerCase()}`}
          </p>
        </div>

        {repeats && (
          <p className="detail__repeat">
            {describeRepeat(repeat, start.toISODate() ?? "", zone)}
          </p>
        )}

        <div className="detail__people">
          {event.people.map((person) => (
            <span
              key={person.id}
              className="chip is-on detail__person"
              style={{
                background: person.color,
                color: foregroundOn(person.color),
                borderColor: person.color,
              }}
            >
              <span
                className="disc chip__disc"
                style={{ background: foregroundOn(person.color), color: person.color }}
              >
                {initialOf(person.display_name)}
              </span>
              {person.display_name}
            </span>
          ))}
        </div>

        {event.location !== null && (
          <section className="detail__block">
            <p className="label">Where</p>
            <p className="detail__value">{event.location}</p>
          </section>
        )}

        {event.notes !== null && (
          <section className="detail__block">
            <p className="label">Notes</p>
            {/* Notes are written with line breaks and are read with them. */}
            <p className="detail__value detail__notes">{event.notes}</p>
          </section>
        )}

        {!canEdit && (
          <p className="detail__readonly">
            You can see the calendar, but not change it.
          </p>
        )}
      </div>
    </div>
  );
}
